import React, { FC, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { WorkLogDetailsType } from '../../../../helpers/ActivityLog.types';
import {
  NewDataGrid,
  setQueryParams,
  TeamFilter,
  useDetailsPageStore,
} from '@devd-client/devd/components';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';

const activityDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const headerDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const formatActivityDate = (value?: string | null) => {
  if (!value) return '';
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return '';
  return activityDateFormatter.format(dateObj);
};

const formatHeaderDate = (value?: string | null) => {
  if (!value) return '';
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return '';
  return headerDateFormatter.format(dateObj);
};

interface SlideDrawerProps {
  openDrawer: boolean;
  workLogDetails?: any;
  handleDrawerClose: () => void;
  workLogDetailsFetching: boolean;
  heading?: string;
  tag: string;
  subHeading: string;
  date?: string;
}

interface NavigationState {
  filterBy: TeamFilter;
  currentChartMetaData: any;
  payload: any;
  tableType: 'PR' | 'ISSUE';
  metricHeading: string;
}

const SlideDrawer: FC<SlideDrawerProps> = ({
  openDrawer,
  workLogDetails,
  handleDrawerClose,
  workLogDetailsFetching,
  heading,
  tag,
  subHeading,
  date,
}) => {
  const { setSelected, setActive } = useDetailsPageStore();
  const navigate = useNavigate();

  let chartKey = '';

  const PRHeadings = ['PR Opened', 'Merged PRs', 'PR reviewed'];

  switch (heading) {
    case 'PR Opened':
      chartKey = 'PR_OPEN';
      break;
    case 'Merged PRs':
      chartKey = 'PR_MERGE';
      break;
    case 'PR reviewed':
      chartKey = 'PR_REVIEWED';
      break;
    default:
      chartKey = '';
      break;
  }

  const columns: ColumnDef<
    WorkLogDetailsType & { activityDateFormatted?: string },
    any
  >[] = useMemo(
    () => [
      {
        header: 'Activity Time',
        accessorKey: 'activityDateFormatted',
        enableSorting: false,
        size: 200,
        cell: ({ row }: any) => {
          const value =
            row.original?.activityDateFormatted || row.original?.activityDate;
          if (!value) return '-';
          return (
            <Text
              fontFamily="heading"
              fontSize="sm"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
              whiteSpace="nowrap"
            >
              {value}
            </Text>
          );
        },
      },
      {
        header: 'Description',
        accessorKey: 'description',
        enableSorting: false,
        size: 320,
        cell: ({ row }: any) => {
          const { description, link } = row.original || {};
          if (!description) return '-';
          return link ? (
            <Link
              href={link}
              target="_blank"
              _hover={{ textDecoration: 'none' }}
            >
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="primary.500"
                fontWeight="medium"
                textDecoration="underline"
                noOfLines={2}
              >
                {description}
              </Text>
            </Link>
          ) : (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={2}
            >
              {description}
            </Text>
          );
        },
      },
      {
        header: 'Author',
        accessorKey: 'assignee',
        enableSorting: false,
        size: 220,
        cell: ({ row }: any) => {
          const assignee = row.original?.assignee;
          if (!assignee) return '-';
          return (
            <Flex align="center">
              <Avatar size="xs" name={assignee} />
              <Text
                ml={2}
                fontSize="sm"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                noOfLines={1}
              >
                {assignee}
              </Text>
            </Flex>
          );
        },
      },
      {
        header: 'Project',
        accessorKey: 'project',
        enableSorting: false,
        size: 200,
        cell: ({ row }: any) => {
          const project = row.original?.project;
          if (!project) return '-';
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {project}
            </Text>
          );
        },
      },
      {
        header: 'Branch',
        accessorKey: 'branch',
        enableSorting: false,
        size: 200,
        cell: ({ row }: any) => {
          const branch = row.original?.branch;
          if (!branch) return '-';
          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {branch}
            </Text>
          );
        },
      },
    ],
    []
  );

  const formattedWorkLogDetails: (WorkLogDetailsType & {
    activityDateFormatted?: string;
  })[] = useMemo(
    () =>
      workLogDetails?.map((item: WorkLogDetailsType) => ({
        ...item,
        activityDateFormatted: formatActivityDate(item.activityDate as string),
      })) ?? [],
    [workLogDetails]
  );

  const subHeaderText = useMemo(() => {
    const prefix =
      tag === 'contributors' ? (heading === 'PR Merged' ? 'of' : 'by') : 'on';
    const entity =
      tag === 'contributors' ? 'user' : tag === 'git' ? 'repo' : 'project';
    const formattedDate = formatHeaderDate(date);

    return formattedDate
      ? `${prefix} ${entity} ${subHeading} on ${formattedDate}`
      : `${prefix} ${entity} ${subHeading}`;
  }, [tag, heading, subHeading, date]);

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" pr={8}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="md"
                fontWeight="semibold"
                color="text.primary"
              >
                {heading}
              </Text>
              <Flex align="center" gap={1}>
                <Text
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="sm"
                  fontFamily="heading"
                >
                  {subHeaderText.split(subHeading)[0]}
                </Text>
                {tag === 'contributors' ? (
                  <RouterLink to={`/contributors/${subHeading}`}>
                    <Text
                      display="inline"
                      textDecoration="underline"
                      fontSize="sm"
                      fontFamily="heading"
                      color="text.secondary"
                      fontWeight="medium"
                    >
                      {subHeading}
                    </Text>
                  </RouterLink>
                ) : (
                  <Text
                    display="inline"
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    {subHeading}
                  </Text>
                )}
                <Text
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="sm"
                  fontFamily="heading"
                >
                  {subHeaderText.split(subHeading)[1]}
                </Text>
              </Flex>
            </Stack>

            {heading && PRHeadings.includes(heading) && (
              <Button
                onClick={() => {
                  const state: NavigationState = {
                    tableType: 'PR',
                    metricHeading: heading ?? '',
                    currentChartMetaData: { chartKey: chartKey },
                    filterBy: { author: subHeading, teamId: '' },
                    payload: null,
                  };

                  setSelected({
                    id: 6,
                    startDate: date as string,
                    endDate: date as string,
                    duration: 'Custom',
                    sprintId: '',
                  });
                  setActive('Custom');
                  navigate(setQueryParams(state));
                }}
                size="sm"
                borderRadius="full"
                px={4}
                minW="120px"
                h="32px"
                fontSize="sm"
                fontWeight="medium"
                fontFamily="heading"
                variant="primary"
              >
                More Details
              </Button>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {workLogDetailsFetching ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="400px"
                maxH="400px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap={4}
                >
                  <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="primary.500"
                    size="lg"
                  />
                  <Text
                    fontFamily="heading"
                    fontSize="sm"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Loading data...
                  </Text>
                </Flex>
              </Box>
            ) : formattedWorkLogDetails &&
              formattedWorkLogDetails.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={formattedWorkLogDetails}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="firstCol"
                  useMaxH
                  showGridLines
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(SlideDrawer);
